// components/AddGuarantorModal.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button, Form, Card, Spinner, Badge, Alert, FloatingLabel } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import {
    FiUserPlus,
    FiSearch,
    FiX,
    FiPhone,
    FiMail,
    FiFileText,
    FiUsers,
    FiPercent,
    FiUser,
    FiSave,
    FiDollarSign,
    FiCalendar,
    FiCreditCard,
    FiPackage,
    FiAlertTriangle
} from "react-icons/fi";

const AddGuarantorModal = ({ show, onHide, refreshData }) => {
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const debounceTimerRef = useRef(null);

    const [guarantorData, setGuarantorData] = useState({
        full_name: "",
        email_address: "",
        phone_number: "",
        date_of_birth: "",
        id_type: "",
        id_number: "",
        passport_number: "",
        guarantor_for: ""
    });

    const performSearch = useCallback(async (term) => {
        try {
            setLoading(true);

            const response = await axios.get(
                `https://www.azzenterprises.com/api/customers/search-by-icnum?id_number=${term}`,
                { withCredentials: true }
            );

            let customers;
            if (Array.isArray(response.data)) {
                customers = response.data;
            } else if (response.data && typeof response.data === 'object') {
                customers = [response.data];
            } else {
                customers = [];
            }

            setSearchResults(customers);
        } catch (error) {
            console.error("Search error:", error);

            if (error.response?.status === 404) {
                setSearchResults([]);
            } else {
                toast.error("Search failed");
                setSearchResults([]);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const debouncedSearch = useCallback((term) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            if (term.length >= 3) {
                performSearch(term);
            } else {
                setSearchResults([]);
            }
        }, 500);
    }, [performSearch]);

    useEffect(() => {
        if (show && searchTerm) {
            debouncedSearch(searchTerm);
        } else if (show) {
            setSearchResults([]);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchTerm, show, debouncedSearch]);

    const resetForm = () => {
        setSearchTerm("");
        setSearchResults([]);
        setSelectedCustomer(null);
        setGuarantorData({
            full_name: "",
            email_address: "",
            phone_number: "",
            date_of_birth: "",
            id_type: "",
            id_number: "",
            passport_number: "",
            guarantor_for: ""
        });
    };

    const handleModalClose = () => {
        resetForm();
        onHide();
    };

    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setSearchResults([]);
        setSearchTerm("");

        setGuarantorData(prev => ({
            ...prev,
            guarantor_for: customer.id_number || ""
        }));
    };

    const handleGuarantorDataChange = (e) => {
        const { name, value } = e.target;
        setGuarantorData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCreateGuarantor = async () => {
        const requiredFields = ['full_name', 'id_number', 'phone_number', 'id_type'];
        const missingFields = requiredFields.filter(field => !guarantorData[field]?.trim());

        if (missingFields.length > 0) {
            toast.warn(`Please fill: ${missingFields.join(', ')}`);
            return;
        }

        if (!selectedCustomer) {
            toast.warn("Please select a customer first");
            return;
        }

        // Check if the guarantor is already a guarantor for someone
        const isAlreadyGuarantor = searchResults.some(customer =>
            customer.id_number === guarantorData.id_number &&
            customer.guarantor_for
        );

        if (isAlreadyGuarantor) {
            const existingCustomer = searchResults.find(c => c.id_number === guarantorData.id_number);
            toast.error(`This person is already a guarantor for ID: ${existingCustomer.guarantor_for}`);
            return;
        }

        try {
            setLoading(true);

            const response = await axios.post(
                "https://www.azzenterprises.com/api/customers/create-guarantor",
                {
                    ...guarantorData
                },
                { withCredentials: true }
            );

            toast.success(`Guarantor created for ${selectedCustomer.full_name}`);

            resetForm();
            onHide();

            if (refreshData) {
                refreshData();
            }
        } catch (error) {
            console.error("Create guarantor error:", error);
            const errorMsg = error.response?.data?.error || "Failed to create guarantor";
            toast.error(`${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const idTypes = [
        { value: "FIN", label: "FIN" },
        { value: "NRIC", label: "NRIC" },
        { value: "PASSPORT", label: "Passport" }
    ];

    const handleSelectIdType = (value) => {
        setGuarantorData(prev => ({
            ...prev,
            id_type: value,
            passport_number: value === "PASSPORT" ? prev.passport_number : ""
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        if (!amount) return '$0.00';
        return `$${parseFloat(amount).toFixed(2)}`;
    };

    return (
        <Modal show={show} onHide={handleModalClose} size="lg" centered backdrop="static">
            <Modal.Header className="border-0 pb-2" style={{ fontSize: '0.9rem' }}>
                <div className="d-flex align-items-center gap-3 w-100">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-3">
                        <FiUserPlus size={20} className="text-primary" />
                    </div>
                    <div>
                        <h6 className="mb-0 fw-bold">Add Guarantor to Customer</h6>
                        <p className="mb-0 text-muted" style={{ fontSize: '0.8rem' }}>
                            Search for a customer, then add their guarantor
                        </p>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="p-0" style={{ fontSize: '0.85rem' }}>
                {!selectedCustomer && (
                    <div className="p-3 border-bottom">
                        <div className="position-relative">
                            <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-2" size={12} />
                            <Form.Control
                                type="text"
                                placeholder="Search customer by ID number..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                                className="ps-4"
                                style={{ fontSize: '0.85rem' }}
                            />
                            {loading && (
                                <Spinner size="sm" className="position-absolute top-50 end-0 translate-middle-y me-2" />
                            )}
                        </div>
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Search for the customer who needs a guarantor
                        </small>
                    </div>
                )}

                {!selectedCustomer && searchResults.length > 0 && (
                    <div className="p-3 border-bottom" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <h6 className="fw-semibold mb-3" style={{ fontSize: '0.85rem' }}>
                            Found {searchResults.length} customer(s)
                        </h6>
                        <div className="row g-3">
                            {searchResults.map((customer, index) => {
                                const isAlreadyGuarantor = customer.guarantor_for ? true : false;

                                return (
                                    <div key={index} className="col-12">
                                        <Card
                                            className="border shadow-sm"
                                            style={{ fontSize: '0.8rem' }}
                                        >
                                            <Card.Body className="p-3">
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>
                                                                {customer.full_name}
                                                            </h6>
                                                            <div
                                                                className={`px-2 py-1 rounded ${isAlreadyGuarantor
                                                                    ? 'bg-danger bg-opacity-25'
                                                                    : 'bg-success bg-opacity-25 text-success'
                                                                    }`}
                                                                style={{ fontSize: '0.7rem' }}
                                                            >
                                                                {isAlreadyGuarantor
                                                                    ? "Has Guarantor"
                                                                    : "Needs Guarantor"}
                                                            </div>
                                                        </div>

                                                        {isAlreadyGuarantor && (
                                                            <Alert variant="danger" className="py-1 mb-2" style={{ fontSize: '0.7rem' }}>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiAlertTriangle size={10} />
                                                                    <span>Already guarantor for ID: {customer.guarantor_for}</span>
                                                                </div>
                                                            </Alert>
                                                        )}

                                                        <div className="row g-2 mb-3">
                                                            <div className="col-6">
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiUser size={12} className="text-dark" />
                                                                    <small>ID: {customer.id_number}</small>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiPhone size={12} className="text-info" />
                                                                    <small>{customer.phone_number}</small>
                                                                </div>
                                                            </div>
                                                            <div className="col-6">
                                                                {customer.email_address && (
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <FiMail size={12} className="text-secondary" />
                                                                        <small>{customer.email_address}</small>
                                                                    </div>
                                                                )}
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiCalendar size={12} className="text-muted" />
                                                                    <small>DOB: {formatDate(customer.date_of_birth)}</small>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="row g-2 mb-3">
                                                            <div className="col-6">
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiDollarSign size={12} className="text-success" />
                                                                    <small>Bill Amt: {formatCurrency(customer.bill_amount)}</small>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiCreditCard size={12} className="text-primary" />
                                                                    <small>Bill Num: {customer.bill_number}</small>
                                                                </div>
                                                            </div>
                                                            <div className="col-6">
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiPackage size={12} className="text-warning" />
                                                                    <small>Product: {customer.product_name}</small>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiCalendar size={12} className="text-info" />
                                                                    <small>EMI: {customer.emi_plan_duration}m</small>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="row g-2 mb-3">
                                                            {customer.boDDARefNo && (
                                                                <div className="col-6">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <FiFileText size={12} className="text-primary" />
                                                                        <small>DDA: {customer.boDDARefNo}</small>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {customer.applicantBankCode && (
                                                                <div className="col-6">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <FiCreditCard size={12} className="text-dark" />
                                                                        <small>Bank: {customer.applicantBankCode}</small>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {customer.boName && (
                                                                <div className="col-6">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <FiUsers size={12} className="text-secondary" />
                                                                        <small>{customer.boName}</small>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {customer.payment_date && (
                                                                <div className="col-6">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <FiCalendar size={12} className="text-warning" />
                                                                        <small>Due date: {new Date(customer.payment_date).getDate()}</small>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {(customer.down_pay || customer.coll_fee) && (
                                                            <div className="d-flex align-items-center gap-1 mb-2">
                                                                <FiPercent size={12} className="text-muted" />
                                                                <small className="text-muted">
                                                                    Fees: {customer.down_pay && `Down: ${customer.down_pay}`} {customer.coll_fee && `Coll: ${customer.coll_fee}`}
                                                                </small>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="ms-3">
                                                        <Button
                                                            variant={customer.has_guarantor === 1 ? "warning" : (isAlreadyGuarantor ? "secondary" : "primary")}
                                                            size="sm"
                                                            onClick={() => handleSelectCustomer(customer)}
                                                            disabled={customer.has_guarantor === 1 || isAlreadyGuarantor}
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            <FiUserPlus size={12} className="me-1" />
                                                            {customer.has_guarantor === 1 ? "Has Guarantor" :
                                                                isAlreadyGuarantor ? "Already Guarantor" : "Add Guarantor"}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {selectedCustomer && (
                    <div className="p-3 border-bottom bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="mb-2 fw-bold" style={{ fontSize: '0.9rem' }}>
                                    Adding Guarantor for:
                                </h6>
                                <div className="row g-2 mb-2">
                                    <div className="col-12">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <FiUser size={14} className="text-primary" />
                                            <strong style={{ fontSize: '0.85rem' }}>{selectedCustomer.full_name}</strong>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center gap-1">
                                            <FiUser size={12} className="text-dark" />
                                            <small>ID: {selectedCustomer.id_number}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                            <FiPhone size={12} className="text-info" />
                                            <small>{selectedCustomer.phone_number}</small>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        {selectedCustomer.boDDARefNo && (
                                            <div className="d-flex align-items-center gap-1">
                                                <FiFileText size={12} className="text-success" />
                                                <small>DDA: {selectedCustomer.boDDARefNo}</small>
                                            </div>
                                        )}
                                        <div className="d-flex align-items-center gap-1">
                                            <FiDollarSign size={12} className="text-success" />
                                            <small>{selectedCustomer.bill_amount || '0'}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                    setSelectedCustomer(null);
                                    setGuarantorData(prev => ({
                                        ...prev,
                                        guarantor_for: ""
                                    }));
                                }}
                                style={{ fontSize: '0.75rem' }}
                            >
                                <FiX size={14} />
                            </Button>
                        </div>
                    </div>
                )}

                {selectedCustomer && (
                    <div className="p-3">
                        <h6 className="fw-bold mb-3" style={{ fontSize: '0.9rem' }}>
                            Guarantor Information
                        </h6>

                        <div className="row">
                            <div className="col-md-6">
                                <FloatingLabel
                                    controlId="full_name"
                                    label="Full Name *"
                                    className="mb-2"
                                    size="sm"
                                >
                                    <Form.Control
                                        type="text"
                                        name="full_name"
                                        value={guarantorData.full_name}
                                        onChange={handleGuarantorDataChange}
                                        size="sm"
                                        style={{ fontSize: '0.85rem' }}
                                        placeholder="Guarantor's full name"
                                    />
                                </FloatingLabel>

                                <FloatingLabel
                                    controlId="email_address"
                                    label="Email Address"
                                    className="mb-2"
                                    size="sm"
                                >
                                    <Form.Control
                                        type="email"
                                        name="email_address"
                                        value={guarantorData.email_address}
                                        onChange={handleGuarantorDataChange}
                                        size="sm"
                                        style={{ fontSize: '0.85rem' }}
                                        placeholder="guarantor@example.com"
                                    />
                                </FloatingLabel>

                                <FloatingLabel
                                    controlId="phone_number"
                                    label="Phone Number *"
                                    className="mb-2"
                                    size="sm"
                                >
                                    <Form.Control
                                        type="tel"
                                        name="phone_number"
                                        value={guarantorData.phone_number}
                                        onChange={handleGuarantorDataChange}
                                        size="sm"
                                        style={{ fontSize: '0.85rem' }}
                                        placeholder="+65 1234 5678"
                                    />
                                </FloatingLabel>

                                <FloatingLabel
                                    controlId="date_of_birth"
                                    label="Date of Birth"
                                    className="mb-2"
                                    size="sm"
                                >
                                    <Form.Control
                                        type="date"
                                        name="date_of_birth"
                                        value={guarantorData.date_of_birth}
                                        onChange={handleGuarantorDataChange}
                                        size="sm"
                                        style={{ fontSize: '0.85rem' }}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-md-6">
                                <div className="mb-2">
                                    <Form.Label style={{ fontSize: '0.8rem' }}>ID Type *</Form.Label>
                                    <div className="d-flex gap-1">
                                        {idTypes.map(type => (
                                            <Button
                                                key={type.value}
                                                variant={guarantorData.id_type === type.value ? "primary" : "outline-primary"}
                                                size="sm"
                                                onClick={() => handleSelectIdType(type.value)}
                                                className="flex-grow-1"
                                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                            >
                                                {type.label}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <FloatingLabel
                                    controlId="id_number"
                                    label="ID Number *"
                                    className="mb-2"
                                    size="sm"
                                >
                                    <Form.Control
                                        type="text"
                                        name="id_number"
                                        value={guarantorData.id_number}
                                        onChange={handleGuarantorDataChange}
                                        size="sm"
                                        style={{ fontSize: '0.85rem' }}
                                        placeholder="S1234567A"
                                    />
                                </FloatingLabel>

                                {guarantorData.id_type === "PASSPORT" && (
                                    <FloatingLabel
                                        controlId="passport_number"
                                        label="Passport Number"
                                        className="mb-2"
                                        size="sm"
                                    >
                                        <Form.Control
                                            type="text"
                                            name="passport_number"
                                            value={guarantorData.passport_number}
                                            onChange={handleGuarantorDataChange}
                                            size="sm"
                                            style={{ fontSize: '0.85rem' }}
                                            placeholder="E12345678"
                                        />
                                    </FloatingLabel>
                                )}

                                <Alert variant="info" className="mt-3 p-2" style={{ fontSize: '0.8rem' }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <FiUserPlus size={14} />
                                        <span>
                                            This guarantor will be created for <strong>{selectedCustomer.full_name}</strong>
                                        </span>
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <div className="d-flex align-items-center gap-1">
                                                <small>Customer ID: {selectedCustomer.id_number}</small>
                                            </div>
                                        </div>
                                        {selectedCustomer.boDDARefNo && (
                                            <div className="col-6">
                                                <div className="d-flex align-items-center gap-1">
                                                    <small>DDA: {selectedCustomer.boDDARefNo}</small>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Alert>
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 p-3" style={{ fontSize: '0.85rem' }}>
                <div className="d-flex justify-content-between w-100">
                    <Button
                        variant="outline-secondary"
                        onClick={handleModalClose}
                        size="sm"
                        style={{ fontSize: '0.8rem' }}
                    >
                        Cancel
                    </Button>

                    {selectedCustomer ? (
                        <Button
                            variant="primary"
                            onClick={handleCreateGuarantor}
                            disabled={loading || !guarantorData.full_name || !guarantorData.id_number}
                            size="sm"
                            style={{ fontSize: '0.8rem' }}
                        >
                            {loading ? (
                                <>
                                    <Spinner size="sm" className="me-1" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <FiSave className="me-1" />
                                    Create Guarantor
                                </>
                            )}
                        </Button>
                    ) : (
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                            Search and select a customer first
                        </div>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default AddGuarantorModal;